/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { BlocksDocument } from '@intercom/embercom-prosemirror-composer';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type HandoffToZendeskTicket from 'embercom/models/operator/visual-builder/step/handoff-to-zendesk-ticket';
import type IntercomAttribute from 'embercom/models/attribute';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import type { Block } from 'embercom/models/common/blocks/block';
import { StandaloneComposerConfig } from 'embercom/objects/standalone/composer-config';
import { get } from 'embercom/lib/ajax';

const MAX_TICKET_TITLE_LENGTH = 150;

interface Args {
  step: HandoffToZendeskTicket;
  onChange: (value: BlocksDocument) => void;
}

interface Signature {
  Args: Args;
}

type ZendeskForm = {
  value: number;
  text: string;
};

export default class Settings extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;
  @service declare attributeService: $TSFixMe;

  @tracked blocksDoc: BlocksDocument;
  @tracked forms: ZendeskForm[] = [];
  @tracked isLoadingForms = true;
  composerConfig: StandaloneComposerConfig;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.blocksDoc = new BlocksDocument(this.args.step.blocks.toArray());

    let attributeResolver = new AttributeInfoResolver({
      attributes: this.appService.app.allowedAttributes,
      attributeAllowlist: this.attributeService.attributesToGroupList(this.allowedAttributes),
      conversationAttributes:
        this.attributeService.nonArchivedConversationCustomAttributesWithoutFile,
      includeCustomObjectAttributes: true,
      includeAppAttributes: false,
    });

    // Initialize composer config in constructor to avoid reactivity issues
    this.composerConfig = new StandaloneComposerConfig({
      resolver: attributeResolver,
      placeholder: this.intl.t(
        'operator.workflows.visual-builder.handoff-to-zendesk-ticket.settings.ticket-title-placeholder',
      ),
      maxCharacterCount: MAX_TICKET_TITLE_LENGTH,
    });

    this.loadForms();
  }

  async loadForms() {
    try {
      this.isLoadingForms = true;
      let response = await get(`/ember/standalone/zendesk_data/available_ticket_forms`, {
        app_id: this.appService.app.id,
      });
      this.forms = response.forms || [];

      // If we have a selected value but it's not in the forms list, clear it.
      if (
        this.args.step.ticketFormId &&
        !this.forms.find((form) => form.value === this.args.step.ticketFormId)
      ) {
        this.args.step.ticketFormId = null;
      }
    } catch (error) {
      console.error('Failed to load Zendesk forms:', error);
      this.forms = [];
    } finally {
      this.isLoadingForms = false;
    }
  }

  get allowedAttributes() {
    return this.attributeService.userEditorMetadataAndConversationAttributes.reject(
      (attr: IntercomAttribute) => attr.isUserEvent,
    );
  }

  get hasContent() {
    return (
      this.blocksDoc &&
      this.blocksDoc.blocks.length > 0 &&
      this.blocksDoc.blocks.some((block: Block) => block.text && block.text.trim().length > 0)
    );
  }

  get selectedFormId() {
    if (!this.args.step.ticketFormId) {
      return undefined;
    }

    // Return undefined if the form is not found.
    // This can happen when the form is deactivated in Zendesk.
    let foundForm = this.forms.find((form) => form.value === this.args.step.ticketFormId);
    return foundForm ? foundForm.value : undefined;
  }

  get hasForms() {
    return this.forms.length > 0;
  }

  @action
  onChange(document: BlocksDocument) {
    this.blocksDoc = document;
    this.args.onChange(document);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::HandoffToZendeskTicket::Settings': typeof Settings;
    'workflows/graph-editor/node-items/steps/handoff-to-zendesk-ticket/settings': typeof Settings;
  }
}
